import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Shield, Bell, Clock, Edit2, Save, MessageCircle, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { profileAPI, notificationsAPI, stockAlertsAPI } from '@/utils/api';
import { toast } from 'sonner';

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [stockAlerts, setStockAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    current_password: '',
    new_password: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [profileRes, transactionsRes, notifRes, alertsRes] = await Promise.all([
        profileAPI.get(),
        profileAPI.getTransactions(),
        notificationsAPI.getAll(),
        stockAlertsAPI.getAll()
      ]);
      
      setProfile(profileRes.data);
      setFormData({
        name: profileRes.data.name,
        email: profileRes.data.email,
        current_password: '',
        new_password: ''
      });
      setTransactions(transactionsRes.data);
      setNotifications(notifRes.data);
      setStockAlerts(alertsRes.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await profileAPI.update(formData);
      toast.success('Profile updated successfully');
      setEditMode(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    }
  };

  const markNotificationRead = async (id) => {
    try {
      await notificationsAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => 
        n.notification_id === id ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark notification as read');
    }
  };

  const markAllRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const removeStockAlert = async (alertId) => {
    try {
      await stockAlertsAPI.delete(alertId);
      setStockAlerts(prev => prev.filter(a => a.alert_id !== alertId));
      toast.success('Stock alert removed');
    } catch (error) {
      toast.error('Failed to remove alert');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-500 bg-green-500/10';
      case 'pending': return 'text-yellow-500 bg-yellow-500/10';
      case 'failed': return 'text-red-500 bg-red-500/10';
      default: return 'text-gray-500 bg-gray-500/10';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Navbar />
      
      <div className="pt-24 pb-16">
        <div className="max-w-5xl mx-auto px-6">
          <h1 className="text-4xl sm:text-5xl font-heading font-black tracking-tight mb-8">
            My Profile
          </h1>

          {/* Customer ID Badge */}
          {profile?.customer_id && (
            <div className="mb-6 p-4 rounded-xl bg-primary/10 border border-primary/20 inline-block">
              <p className="text-sm text-muted-foreground">Customer ID</p>
              <p className="text-2xl font-heading font-black text-primary" data-testid="customer-id">
                {profile.customer_id}
              </p>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-white/10 overflow-x-auto pb-2">
            {['profile', 'transactions', 'notifications', 'alerts', 'support'].map((tab) => (
              <Button
                key={tab}
                variant={activeTab === tab ? 'default' : 'ghost'}
                onClick={() => setActiveTab(tab)}
                className="capitalize rounded-full"
                data-testid={`${tab}-tab`}
              >
                {tab === 'alerts' ? 'Stock Alerts' : tab}
              </Button>
            ))}
          </div>

          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="p-6 rounded-2xl border border-white/10 bg-white/5" data-testid="profile-section">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-heading font-bold">Account Information</h2>
                <Button
                  variant="outline"
                  onClick={() => setEditMode(!editMode)}
                  className="rounded-full"
                  data-testid="edit-profile-btn"
                >
                  {editMode ? 'Cancel' : <><Edit2 className="w-4 h-4 mr-2" /> Edit</>}
                </Button>
              </div>

              {editMode ? (
                <form onSubmit={handleUpdateProfile} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-white/5 border-white/10 mt-2"
                      data-testid="name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-white/5 border-white/10 mt-2"
                      data-testid="email-input"
                    />
                  </div>
                  <div className="pt-4 border-t border-white/10">
                    <h3 className="font-bold mb-4">Change Password</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="current_password">Current Password</Label>
                        <Input
                          id="current_password"
                          type="password"
                          value={formData.current_password}
                          onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
                          className="bg-white/5 border-white/10 mt-2"
                          data-testid="current-password-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="new_password">New Password</Label>
                        <Input
                          id="new_password"
                          type="password"
                          value={formData.new_password}
                          onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                          className="bg-white/5 border-white/10 mt-2"
                          data-testid="new-password-input"
                        />
                      </div>
                    </div>
                  </div>
                  <Button type="submit" className="btn-primary-glow rounded-full" data-testid="save-profile-btn">
                    <Save className="w-4 h-4 mr-2" /> Save Changes
                  </Button>
                </form>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5">
                    <User className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{profile?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5">
                    <Mail className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="font-medium">{profile?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5">
                    <Shield className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Account Type</p>
                      <p className="font-medium capitalize">{profile?.role}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5">
                    <Clock className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Member Since</p>
                      <p className="font-medium">
                        {new Date(profile?.created_at).toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Transactions Tab */}
          {activeTab === 'transactions' && (
            <div data-testid="transactions-section">
              <h2 className="text-2xl font-heading font-bold mb-6">Transaction History</h2>
              {transactions.length === 0 ? (
                <div className="p-8 rounded-2xl border border-white/10 bg-white/5 text-center">
                  <p className="text-muted-foreground">No transactions yet</p>
                  <Button onClick={() => navigate('/')} className="mt-4 rounded-full">
                    Start Shopping
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {transactions.map((order) => (
                    <div
                      key={order.order_id}
                      className="p-6 rounded-2xl border border-white/10 bg-white/5"
                      data-testid={`transaction-${order.order_id}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-mono text-sm text-muted-foreground">
                            Order #{order.order_id}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(order.created_at).toLocaleDateString('en-IN', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(order.status)}`}>
                          {order.status.toUpperCase()}
                        </span>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        {order.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{item.title} (x{item.quantity})</span>
                            <span>₹{item.price * item.quantity}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex justify-between pt-4 border-t border-white/10">
                        <span className="font-bold">Total</span>
                        <span className="font-heading font-black text-xl">
                          ₹{order.final_amount || order.total_amount}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && (
            <div data-testid="notifications-section">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-heading font-bold">Notifications</h2>
                {notifications.some(n => !n.read) && (
                  <Button variant="outline" onClick={markAllRead} className="rounded-full text-sm">
                    Mark all as read
                  </Button>
                )}
              </div>
              
              {notifications.length === 0 ? (
                <div className="p-8 rounded-2xl border border-white/10 bg-white/5 text-center">
                  <Bell className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No notifications yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div
                      key={notif.notification_id}
                      className={`p-4 rounded-xl border cursor-pointer transition-colors ${
                        notif.read 
                          ? 'border-white/10 bg-white/5' 
                          : 'border-primary/30 bg-primary/10'
                      }`}
                      onClick={() => markNotificationRead(notif.notification_id)}
                      data-testid={`notification-${notif.notification_id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold">{notif.title}</p>
                          <p className="text-sm text-muted-foreground mt-1">{notif.message}</p>
                        </div>
                        {!notif.read && (
                          <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2"></span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        {new Date(notif.created_at).toLocaleDateString('en-IN', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Stock Alerts Tab */}
          {activeTab === 'alerts' && (
            <div data-testid="alerts-section">
              <h2 className="text-2xl font-heading font-bold mb-6">Stock Alerts</h2>
              <p className="text-muted-foreground mb-6">
                Get notified when out-of-stock products become available.
              </p>
              
              {stockAlerts.length === 0 ? (
                <div className="p-8 rounded-2xl border border-white/10 bg-white/5 text-center">
                  <Bell className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">No stock alerts set</p>
                  <Button onClick={() => navigate('/')} className="mt-4 rounded-full">
                    Browse Products
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {stockAlerts.map((alert) => (
                    <div
                      key={alert.alert_id}
                      className="p-4 rounded-xl border border-white/10 bg-white/5 flex justify-between items-center"
                      data-testid={`alert-${alert.alert_id}`}
                    >
                      <div>
                        <p className="font-bold">{alert.account_title}</p>
                        <p className="text-sm text-muted-foreground">
                          {alert.notified ? 'Notified' : 'Waiting for stock'}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStockAlert(alert.alert_id)}
                        className="text-destructive"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Support Tab */}
          {activeTab === 'support' && (
            <div data-testid="support-section">
              <h2 className="text-2xl font-heading font-bold mb-6">Support</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Telegram Support */}
                <a
                  href="https://t.me/xtreamstore"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-6 rounded-2xl border border-white/10 bg-white/5 hover:border-primary/50 transition-colors block"
                  data-testid="telegram-support"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-xl bg-[#0088cc]/20">
                      <MessageCircle className="w-6 h-6 text-[#0088cc]" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg">Telegram Support</h3>
                      <p className="text-sm text-muted-foreground">Get instant help</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm mb-4">
                    Connect with us on Telegram for quick support, updates, and exclusive offers.
                  </p>
                  <div className="flex items-center text-primary font-bold">
                    Open Telegram <ExternalLink className="w-4 h-4 ml-2" />
                  </div>
                </a>

                {/* Help Center */}
                <div
                  className="p-6 rounded-2xl border border-white/10 bg-white/5 hover:border-primary/50 transition-colors cursor-pointer"
                  onClick={() => navigate('/help-center')}
                  data-testid="help-center"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-xl bg-primary/20">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg">Help Center</h3>
                      <p className="text-sm text-muted-foreground">FAQs & Guides</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm mb-4">
                    Find answers to common questions about orders, payments, and account issues.
                  </p>
                  <div className="flex items-center text-primary font-bold">
                    View Help Center <ExternalLink className="w-4 h-4 ml-2" />
                  </div>
                </div>

                {/* Email Support */}
                <a
                  href="mailto:support@xtreamstore.in"
                  className="p-6 rounded-2xl border border-white/10 bg-white/5 hover:border-primary/50 transition-colors block"
                  data-testid="email-support"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-xl bg-accent/20">
                      <Mail className="w-6 h-6 text-accent" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg">Email Support</h3>
                      <p className="text-sm text-muted-foreground">support@xtreamstore.in</p>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm mb-4">
                    Send us an email for detailed inquiries. We typically respond within 24 hours.
                  </p>
                  <div className="flex items-center text-primary font-bold">
                    Send Email <ExternalLink className="w-4 h-4 ml-2" />
                  </div>
                </a>

                {/* Response Time */}
                <div className="p-6 rounded-2xl border border-white/10 bg-white/5">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 rounded-xl bg-green-500/20">
                      <Clock className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h3 className="font-heading font-bold text-lg">Response Time</h3>
                      <p className="text-sm text-muted-foreground">Quick Support</p>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Telegram:</span>
                      <span className="font-bold text-green-500">Within minutes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email:</span>
                      <span className="font-bold">24-48 hours</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ProfilePage;
